import { BookOpenCheck, Rocket, School, type LucideIcon } from "lucide-react";
import { cn } from "@eduviaos/ui-kit";
import type { Vertical } from "../lib/institution";
import { useTenantBrandingContext } from "./useTenantBrandingContext";

/**
 * Production institution mark — renders the tenant's REAL uploaded logo
 * (Phase F2c) when the branding API provides one, else falls back to a
 * tenant-tinted rounded tile with a vertical-aware SVG glyph. The
 * `logo_url` comes from `<TenantBrandingProvider>` context, so callers
 * don't change when a tenant uploads a logo — the glyph swaps to an
 * `<img>` transparently.
 */

const VERTICAL_GLYPH: Record<Vertical, LucideIcon> = {
  school: School,
  coaching_institute: Rocket,
  study_centre: BookOpenCheck,
};

const SIZE_MAP = {
  sm: { box: "size-9 rounded-xl", icon: "size-5" },
  md: { box: "size-11 rounded-2xl", icon: "size-6" },
  lg: { box: "size-14 rounded-2xl", icon: "size-7" },
} as const;

export function InstitutionMark({
  vertical,
  name,
  size = "sm",
  className,
}: {
  vertical: Vertical;
  /** Institution name — used for the aria-label only. */
  name?: string;
  size?: keyof typeof SIZE_MAP;
  className?: string;
}) {
  const { branding } = useTenantBrandingContext();
  const s = SIZE_MAP[size];
  const label = name ? `${name} logo` : "Institution logo";

  if (branding?.logo_url) {
    return (
      <img
        src={branding.logo_url}
        alt={label}
        className={cn(
          "bg-background ring-border/60 shrink-0 object-contain ring-1",
          s.box,
          className,
        )}
      />
    );
  }

  const Glyph = VERTICAL_GLYPH[vertical];
  return (
    <span
      role="img"
      aria-label={label}
      className={cn(
        "bg-primary/12 text-primary ring-primary/15 grid shrink-0 place-items-center ring-1",
        s.box,
        className,
      )}
    >
      <Glyph className={s.icon} strokeWidth={2.25} />
    </span>
  );
}
